import { useState } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import {
  Calendar, ChevronDown, ChevronUp, BookOpen, Mic, Flame, Zap, Star,
  CheckCircle2, ChevronRight, AlertCircle,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate } from '../../utils/cabinI18n';
import { useDailyTasks } from '../../hooks/cabin/useDailyTasks';
import { useClaimDailyTask } from '../../hooks/cabin/useClaimDailyTask';
import { useConfetti } from '../../hooks/cabin/useConfetti';
import { useSoundEffects } from '../../hooks/cabin/useSoundEffects';

const ICON_MAP = {
  BookOpen,
  Mic,
  Flame,
  Zap,
  Star,
  Calendar,
};

function getIcon(iconName) {
  return ICON_MAP[iconName] || BookOpen;
}

function SkeletonTask() {
  return (
    <div className="p-3 rounded-xl border-2 border-gray-100 bg-gray-50 animate-pulse">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-8 h-8 rounded-lg bg-gray-200 shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3.5 bg-gray-200 rounded w-2/3" />
          <div className="h-2.5 bg-gray-200 rounded w-1/2" />
        </div>
        <div className="w-16 h-7 bg-gray-200 rounded-lg" />
      </div>
      <div className="h-2 bg-gray-200 rounded-full" />
    </div>
  );
}

function TaskCard({ task, t, lang, onClaim, isClaiming }) {
  const shouldReduce = useReducedMotion();
  const IconComp = getIcon(task.icon);

  const title = lang === 'tr' ? task.title_tr : (task.title_en || task.title_tr);
  const description = lang === 'tr' ? task.description_tr : (task.description_en || task.description_tr);

  const progress = Math.min((task.current_value / Math.max(task.target_value, 1)) * 100, 100);
  const isCompleted = task.status === 'completed';
  const isClaimed = task.status === 'claimed';
  const isDone = isCompleted || isClaimed;

  const cardBg = isDone ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200';
  const iconBg = isDone ? 'bg-emerald-100' : 'bg-sky-50';
  const iconColor = isDone ? 'text-emerald-500' : 'text-sky-500';

  return (
    <motion.div
      layout
      initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-3 rounded-xl border-2 transition-colors ${cardBg}`}
      data-testid={`daily-task-card-${task.id}`}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
          <IconComp size={16} className={iconColor} aria-hidden="true" />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[var(--text-main)] truncate">{title}</p>
          <p className="text-xs text-[var(--text-muted)] truncate">{description}</p>
        </div>

        {/* CTA */}
        {isClaimed ? (
          <span
            className="flex items-center gap-1 text-xs font-bold text-emerald-600 shrink-0"
            aria-label={t('cabin.gamification.dailyTasks.claimed') || 'Tamamlandı'}
          >
            <CheckCircle2 size={14} aria-hidden="true" />
            {t('cabin.gamification.dailyTasks.claimed') || 'Tamamlandı'}
          </span>
        ) : isCompleted ? (
          <motion.button
            whileHover={shouldReduce ? {} : { scale: 1.03 }}
            whileTap={shouldReduce ? {} : { scale: 0.97 }}
            onClick={() => onClaim(task.id)}
            disabled={isClaiming}
            data-testid={`claim-task-${task.id}`}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-500 text-white text-xs font-bold rounded-lg shadow-[0_2px_0_0_#059669] hover:bg-emerald-600 transition-colors disabled:opacity-60 shrink-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500"
            aria-label={t('cabin.gamification.dailyTasks.claim_reward') || 'Ödülü al'}
          >
            <Star size={12} aria-hidden="true" />
            {t('cabin.gamification.dailyTasks.claim_reward') || 'Ödülü al'}
          </motion.button>
        ) : (
          <span
            className="flex items-center gap-0.5 text-xs text-[var(--text-muted)] shrink-0"
            aria-label={`${task.current_value}/${task.target_value}`}
          >
            {task.current_value}/{task.target_value}
            <ChevronRight size={12} aria-hidden="true" />
          </span>
        )}
      </div>

      {/* Progress bar */}
      {!isClaimed && (
        <div
          className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={task.current_value}
          aria-valuemin={0}
          aria-valuemax={task.target_value}
          aria-label={interpolate(
            t('cabin.gamification.dailyTasks.progress_aria') || '{current}/{total} tamamlandı',
            { current: task.current_value, total: task.target_value }
          )}
        >
          <motion.div
            className={`h-full rounded-full ${isDone ? 'bg-emerald-500' : 'bg-sky-500'}`}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: shouldReduce ? 0 : 0.5, ease: 'easeOut' }}
          />
        </div>
      )}

      {/* Reward chips */}
      {!isClaimed && (
        <div className="mt-1.5 flex items-center gap-2">
          {task.reward_xp > 0 && (
            <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-md">
              +{task.reward_xp} XP
            </span>
          )}
          {task.reward_miles > 0 && (
            <span className="text-[10px] font-bold text-sky-600 bg-sky-50 px-1.5 py-0.5 rounded-md">
              +{task.reward_miles} {t('cabin.gamification.dailyTasks.miles_unit') || 'Mil'}
            </span>
          )}
        </div>
      )}
    </motion.div>
  );
}

/**
 * DailyTasksWidget — M5.4 full implementation.
 * Web: expanded by default (spec).
 * Mobile: collapsed by default (handled via defaultExpanded prop).
 */
export default function DailyTasksWidget({ defaultExpanded = true }) {
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [claimedIds, setClaimedIds] = useState([]);

  const { data, isLoading, isError } = useDailyTasks();
  const { mutate: claimTask, isPending: isClaiming } = useClaimDailyTask();
  const { fireSmall } = useConfetti();
  const { play } = useSoundEffects();

  const tasks = data?.tasks || [];
  const completedCount = data?.completed_count ?? tasks.filter((t) => t.status === 'claimed').length;
  const totalCount = data?.total_count ?? tasks.length;
  const overallProgress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleClaim = (taskId) => {
    claimTask(taskId, {
      onSuccess: () => {
        setClaimedIds((prev) => [...prev, taskId]);
        if (!shouldReduce) {
          fireSmall({ y: 0.5 });
        }
        play('coin');
      },
    });
  };

  return (
    <div
      className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] overflow-hidden"
      data-testid="cabin-daily-tasks-widget"
    >
      {/* Header */}
      <button
        className="w-full flex items-center justify-between p-4 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="daily-tasks-body"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Calendar size={18} className="text-[var(--brand-primary)] shrink-0" aria-hidden="true" strokeWidth={2.5} />
          <span className="text-sm font-bold text-[var(--text-main)] truncate">
            {t('cabin.gamification.dailyTasks.title') || t('cabin.daily_tasks.title') || 'Günlük Görevler'}
          </span>

          {/* Overall badge */}
          {totalCount > 0 && (
            <span
              className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-black shrink-0 ${
                completedCount === totalCount
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-sky-100 text-sky-700'
              }`}
              aria-label={interpolate(
                t('cabin.gamification.dailyTasks.overall_aria') || '{done}/{total} görev',
                { done: completedCount, total: totalCount }
              )}
            >
              {completedCount}/{totalCount}
            </span>
          )}
        </div>

        {expanded
          ? <ChevronUp size={16} className="text-[var(--text-muted)] shrink-0" aria-hidden="true" />
          : <ChevronDown size={16} className="text-[var(--text-muted)] shrink-0" aria-hidden="true" />
        }
      </button>

      {/* Overall progress bar */}
      {expanded && totalCount > 0 && (
        <div className="px-4 pb-1">
          <div
            className="h-1.5 bg-gray-100 rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={completedCount}
            aria-valuemin={0}
            aria-valuemax={totalCount}
            aria-label={interpolate(
              t('cabin.gamification.dailyTasks.overall_progress') || 'Genel ilerleme: {done}/{total}',
              { done: completedCount, total: totalCount }
            )}
          >
            <motion.div
              className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress}%` }}
              transition={{ duration: shouldReduce ? 0 : 0.6, ease: 'easeOut' }}
            />
          </div>
        </div>
      )}

      <AnimatePresence>
        {expanded && (
          <motion.div
            id="daily-tasks-body"
            key="tasks-body"
            initial={shouldReduce ? {} : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={shouldReduce ? {} : { height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-2 flex flex-col gap-2.5">
              {isLoading ? (
                <>
                  <SkeletonTask />
                  <SkeletonTask />
                  <SkeletonTask />
                </>
              ) : isError ? (
                <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm">
                  <AlertCircle size={16} aria-hidden="true" />
                  {t('cabin.gamification.dailyTasks.error') || 'Görevler yüklenemedi'}
                </div>
              ) : tasks.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] text-center py-3">
                  {t('cabin.gamification.dailyTasks.empty') || 'Bugün için görev yok'}
                </p>
              ) : (
                tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={claimedIds.includes(task.id) ? { ...task, status: 'claimed' } : task}
                    t={t}
                    lang={lang}
                    onClaim={handleClaim}
                    isClaiming={isClaiming}
                  />
                ))
              )}

              {/* Reset notice */}
              {data?.reset_at && (
                <p className="text-[10px] text-[var(--text-muted)] text-center">
                  {t('cabin.gamification.dailyTasks.resets_at') || 'Görevler gece 00:00\'da sıfırlanır'}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
